<template>
    <div class="ask-pendo-conversation">
        <pendo-page-header class="ask-pendo-conversation__header">
            <template #title>
                <h1 v-if="isCustomerFinder">
                    {{ $t('askPendo.Customer_finder') }}
                </h1>
                <h1 v-else-if="isListenAgent">
                    Listen Agent
                </h1>
                <h1 v-else>
                    {{ $t('askPendo.Agent mode') }}
                </h1>
            </template>
            <template #tags>
                <pendo-tag
                    size="small"
                    type="beta"
                    class="ask-pendo-conversation__title-tag"
                    :pendo-feature="`ask-pendo-beta-tag${pendoFeatureSuffix}`" />
            </template>
            <template #actions>
                <div class="ask-pendo-conversation__actions">
                    <ask-pendo-saved-chat />
                    <pendo-button
                        theme="app"
                        type="secondary"
                        :label="$t('askPendo.New conversation')"
                        :pendo-feature="`new-conversation-button${pendoFeatureSuffix}`"
                        @click="handleNewConversation" />
                </div>
            </template>
        </pendo-page-header>
        <pendo-page-content class="ask-pendo-conversation__content">
            <split-pane
                class="ask-pendo-conversation__pane"
                :initial-size-leading="0.35"
                :min-size-leading="350"
                :min-size-trailing="350"
                :hide-trailing="hideArtifactPane">
                <template #leading>
                    <div class="ask-pendo-conversation__thread">
                        <ask-pendo-thread
                            ref="thread"
                            :messages="messages"
                            :global-filters="globalFilters"
                            :conversation-id="id"
                            :agent-id="agentId"
                            :pending="pending"
                            :typing="typing"
                            :error="errorMessage"
                            :show-next-actions="isCustomerFinder"
                            :show-message-timestamps="true"
                            :plan="plan"
                            @update="handleUpdate"
                            @stop="handleStop"
                            @retry="handleRetry">
                            <template #reasoning="{ message }">
                                <ask-pendo-reasoning
                                    v-if="reasoningUi(message).show"
                                    :messages="reasoningUi(message).msgs"
                                    :pending="reasoningUi(message).pending"
                                    @visible-messages-changed="scrollThreadToBottom" />
                            </template>
                            <template #artifact="{ message }">
                                <div v-if="inlineArtifacts && getNonNextActionArtifacts(message).length > 0">
                                    <ask-pendo-artifact
                                        v-for="artifact in getNonNextActionArtifacts(message)"
                                        :key="artifact.artifact_id"
                                        class="ask-pendo-conversation__artifact-inline"
                                        :artifact="artifact"
                                        is-inline
                                        :is-streaming="isTypingMessage(message)"
                                        @toggle="
                                            inlineArtifacts = false;
                                            activeArtifactId = artifact.artifact_id;
                                        " />
                                </div>
                                <div v-if="!inlineArtifacts && getNonNextActionArtifacts(message).length > 0">
                                    <ask-pendo-artifact-card
                                        v-for="artifact in getNonNextActionArtifacts(message)"
                                        :key="artifact.artifact_id"
                                        :active="activeArtifactId === artifact.artifact_id"
                                        :artifact="artifact"
                                        @click="activeArtifactId = artifact.artifact_id" />
                                </div>
                                <div
                                    v-if="getNextActionArtifacts(message).length > 0"
                                    class="ask-pendo-conversation__next-actions">
                                    <div class="ask-pendo-conversation__next-actions-heading">
                                        {{ getNextActionsHeading(message) }}
                                    </div>
                                    <ol class="ask-pendo-conversation__next-actions-list">
                                        <li
                                            v-for="artifact in getNextActionArtifacts(message)"
                                            :key="artifact.artifact_id">
                                            <ask-pendo-next-actions-link
                                                :artifact="artifact"
                                                @click="handleNextAction(artifact)" />
                                        </li>
                                    </ol>
                                </div>
                            </template>
                        </ask-pendo-thread>
                    </div>
                </template>
                <template #trailing>
                    <div
                        v-if="activeArtifact"
                        class="ask-pendo-conversation__artifacts">
                        <ask-pendo-artifact
                            :artifact="activeArtifact"
                            :is-streaming="activeArtifact === streamingArtifact"
                            @toggle="clearActiveArtifact">
                            <template #title>
                                <ask-pendo-artifact-multiselect
                                    :active-item-id="activeArtifactId"
                                    :items="artifactMultiselectItems"
                                    @select="setActiveArtifact" />
                            </template>
                        </ask-pendo-artifact>
                    </div>
                </template>
            </split-pane>
        </pendo-page-content>
        <edit-segment-overlay
            :visible="showSegmentOverlay"
            :is-ask-pendo-segment="true"
            @close="onCloseSegmentOverlay"
            @save="onSaveSegment" />
    </div>
</template>

<script>
import { PendoButton, PendoPageContent, PendoPageHeader, PendoTag, PendoNotification } from '@pendo/components';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import AskPendoArtifact from '@/ask-pendo/components/ask-pendo-artifact.vue';
import AskPendoArtifactCard from '@/ask-pendo/components/ask-pendo-artifact-card.vue';
import AskPendoNextActionsLink from '@/ask-pendo/components/ask-pendo-next-action-link.vue';
import AskPendoReasoning from '@/ask-pendo/components/ask-pendo-reasoning.vue';
import AskPendoThread from '@/ask-pendo/components/ask-pendo-thread.vue';
import AskPendoSavedChat from '@/ask-pendo/components/ask-pendo-saved-chat.vue';
import EditSegmentOverlay from '@/segments/components/segment-editor-vue/edit-segment-overlay.vue';
import SplitPane from '@/common/split-pane/split-pane.vue';
import {
    hasReasoningSummaries,
    isAssistantMessage,
    isCustomInstructionsMessage,
    isErrorMessage,
    isNextActionsProgress,
    isProgressMessage,
    isToolCall,
    isToolCallArgumentStream,
    isToolResponse,
    isUserMessage,
    isSubagentCall,
} from '@/ask-pendo/utils/message';
import { AT_MENTIONS_SEQUENCE, CLICKABLE_OPTION_SEQUENCE, TypingService } from '@/ask-pendo/utils/typing-service';
import { buildPayload, leoAgentId, trackAgentEvent } from '@/ask-pendo/utils/agent-analytics';
import { parseMessageContent, appendGuideNextActionArtifact, putAskPendoConversation } from '@/vuex/modules/ask-pendo/ask-pendo.utils';
import { getArtifactIcon, getArtifactTitle, getOrderedArtifacts, transformArtifactToFilters } from '@/ask-pendo/artifacts/artifact.utils';
import { EventBus } from '@utils/event-bus';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import AskPendoArtifactMultiselect from '@/ask-pendo/components/ask-pendo-artifact-multiselect.vue';

export default {
    name: 'AskPendoConversation',
    components: {
        AskPendoArtifactMultiselect,
        AskPendoArtifact,
        AskPendoArtifactCard,
        AskPendoNextActionsLink,
        AskPendoSavedChat,
        AskPendoReasoning,
        AskPendoThread,
        EditSegmentOverlay,
        PendoButton,
        PendoPageContent,
        PendoPageHeader,
        PendoTag,
        SplitPane,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    emits: ['artifact-pane-change'],
    data () {
        const typingService = new TypingService({
            blockingSequences: [AT_MENTIONS_SEQUENCE, CLICKABLE_OPTION_SEQUENCE],
            onEmitText: (text) => {
                this.typingMessage.content += text;
            },
            onTypingStateChange: (isTyping) => {
                // Typing has finished and the request has completed, so no more data will
                // be streaming in. Reset to prepare for next turn.
                if (!isTyping && !this.pending) {
                    this.reset();
                }
            },
        });

        return {
            activeArtifactId: null,
            errorMessage: null,
            toolMessages: [],
            typingMessage: {
                id: 'typing-message',
                type: 'assistant',
                tool_calls: [],
                metadata: {
                    isTypingMessage: true,
                },
                content: '',
            },
            reasoningStreamMessage: {
                id: 'reasoning-stream',
                type: 'reasoning',
                content: '',
            },
            lastReasoningIndex: -1,
            reasoningHeaderParsed: false,
            fullMessageReceived: false, // Track if backend has sent complete message
            isCustomerFinder: false,
            isAgentMode: false,
            isListenAgent: false,
            typingService,
            inlineArtifacts: true,
            showSegmentOverlay: false,
            segmentOverlayData: null,
            nextActionsProcessing: false,
            nextActionsMessage: null,
            persistentNextActionsMessages: [],
            persistentCustomInstructionsMessages: [],
            continueStreaming: false, // Track if we're in a multi-part stream waiting for more content
            streamEndComplete: false, // Track if handleStreamEnd has completed - handles race condition to prevent typing message from reappearing
        };
    },
    computed: {
        ...mapGetters('askPendo', [
            'isResponsePending',
            'awaitingFirstResponse',
            'conversationById',
            'getEmployeeConfig',
        ]),
        ...mapGetters('auth', ['hasSegmentFlag']),
        pendoFeatureSuffix () {
            return this.isCustomerFinder ? '--customer-finder' : '';
        },
        conversation () {
            return this.conversationById(this.id);
        },
        plan () {
            const plan = this.conversation?.plan;
            if (!isEmpty(plan)) {
                return { id: plan.id, todos: plan.todos };
            }

            const workflow = this.conversation?.workflow_state;
            if (!isEmpty(workflow)) {
                return { id: workflow.id, todos: workflow.todo_list };
            }

            return { id: null, todos: [] };
        },
        agentId () {
            return leoAgentId({ isCustomerFinder: this.isCustomerFinder, isListenAgent: this.isListenAgent });
        },
        messages () {
            const convoMessages = this.conversation?.messages || [];
            const typingMessages = [];

            if ((this.pending || this.typingStarted) && !this.streamEndComplete) {
                typingMessages.push(this.typingMessage);
            }

            return [...convoMessages, ...this.persistentCustomInstructionsMessages, ...this.persistentNextActionsMessages, ...typingMessages];
        },
        activeArtifact () {
            if (this.streamingArtifact?.artifact_id === this.activeArtifactId) {
                return this.streamingArtifact;
            }

            return this.activeArtifactId
                ? this.conversation?.artifacts?.[this.activeArtifactId]
                : null;
        },
        streamingArtifact () {
            return this.typingMessage.metadata?.artifact;
        },
        globalFilters () {
            return this.conversation?.global_filters || {};
        },
        pending () {
            return this.isResponsePending(this.id);
        },
        lastAssistantMessage () {
            const msgs = this.conversation?.messages || [];

            return [...msgs].reverse().find((msg) => msg?.type === 'assistant');
        },
        /**
         * A map of assistant message ids to the list of tool calls + responses that are
         * associated with that assistant message.
         */
        reasoningMessagesById () {
            const reasoningMessagesById = {};

            let currentReasoningMessages = [];

            const push = (message) => {
                currentReasoningMessages.push(message);
            };

            const flush = (messageId) => {
                if (currentReasoningMessages.length) {
                    reasoningMessagesById[messageId] = currentReasoningMessages;
                }
            };

            const reset = () => {
                currentReasoningMessages = [];
            };

            this.messages.forEach((message) => {
                if (isUserMessage(message) || isErrorMessage(message)) {
                    reset();
                }

                // Skip messages of the supervisor calling subagents, as we use the subagent tool calls instead
                if (isSubagentCall(message)) {
                    return;
                }

                if (isToolCall(message) || isToolResponse(message) || isCustomInstructionsMessage(message)) {
                    push(message);
                }
                if (isAssistantMessage(message)) {
                    if (hasReasoningSummaries(message)) {
                        push(message);
                    }

                    flush(message.id);
                    reset();
                }
                // Include next_actions progress messages using their stored assistantId
                // This allows them to be associated correctly even when appended at the end of messages array
                if (isNextActionsProgress(message)) {
                    const assistantId = message.metadata?.assistantId;
                    if (assistantId) {
                        if (!reasoningMessagesById[assistantId]) {
                            reasoningMessagesById[assistantId] = [];
                        }
                        reasoningMessagesById[assistantId].push(message);
                    }
                }
                // Include custom_instructions messages using their stored assistantId
                // These are persisted after streaming ends and need to be associated with the correct assistant message
                if (isCustomInstructionsMessage(message) && message.metadata?.assistantId) {
                    const { assistantId } = message.metadata;
                    if (!reasoningMessagesById[assistantId]) {
                        reasoningMessagesById[assistantId] = [];
                    }
                    // Add to the beginning so it appears first in reasoning
                    reasoningMessagesById[assistantId].unshift(message);
                }
            });

            return reasoningMessagesById;
        },
        typing () {
            return this.typingService.isTyping;
        },
        typingStarted () {
            return this.typingService.isTypingStarted;
        },
        hideArtifactPane () {
            if (this.inlineArtifacts) return true;

            return !this.activeArtifact;
        },
        landingPagePath () {
            if (this.isCustomerFinder) return '/leo/customer-finder';
            if (this.isListenAgent) return '/leo/listen';

            return '/agent-mode';
        },
        artifactMultiselectItems () {
            const items = getOrderedArtifacts(this.conversation)
                .filter(
                    ({ artifact }) => artifact.type !== 'next_actions',
                )
                .map(({ messageId, artifact }) => ({
                    id: artifact.artifact_id,
                    messageId,
                    label: getArtifactTitle(artifact),
                    icon: getArtifactIcon(artifact),
                }));

            const streaming = this.streamingArtifact;
            if (streaming && streaming.artifact_id) {
                items.push({
                    id: streaming.artifact_id,
                    messageId: this.typingMessage.id,
                    label: streaming.name || 'Artifact generating...',
                    icon: getArtifactIcon(streaming),
                });
            }

            return items;
        },
        messageMetadata () {
            return {
                state: {
                    global_filters: {
                        segment_id: this.conversation?.global_filters?.segment_id || 'everyone',
                    },
                },
            };
        },
    },
    watch: {
        'conversation.last_artifact_id': function (artifactId) {
            if (artifactId) {
                this.activeArtifactId = artifactId;
            }
        },
        hideArtifactPane (hidden) {
            this.$emit('artifact-pane-change', !hidden);
        },
    },
    async created () {
        this.isCustomerFinder = this.$route.name === 'CustomerFinderConversation';
        this.isAgentMode = this.$route.name === 'AgentModeConversation';
        this.isListenAgent = this.$route.name === 'ListenAgentConversation';
        this.loadPages();
        this.loadFeatures();

        const path = this.landingPagePath;

        // Try to load the conversation if it doesn't exist in the store
        if (!this.conversation) {
            try {
                await this.loadOne({ id: this.id });
            } catch {
                // If loading fails (e.g., conversation doesn't exist), redirect to landing page
                this.$router.push({ path, query: { ...this.$route.query } });

                return;
            }
        }

        if (this.awaitingFirstResponse(this.id)) {
            try {
                await this.sendConversationRequest();
            } catch (error) {
                if (error.message.includes('Conversation not found')) {
                    this.$router.push({ path, query: { ...this.$route.query } });
                } else {
                    this.reset();
                    this.errorMessage = error.message || 'An error occurred while loading the conversation.';
                }
            }
        }
    },
    methods: {
        ...mapActions('askPendo', ['sendConversation', 'addMessage', 'stopResponse', 'removeLastExchange', 'loadOne']),
        ...mapMutations('askPendo', ['updateArtifact']),
        ...mapActions({
            loadPages: 'pages/loadAll',
            loadFeatures: 'features/loadAll',
            createSegmentWithDefinition: 'segments/createSegmentWithDefinition',
        }),
        reset (clearCompletedMessages = true) {
            this.typingMessage = {
                id: 'typing-message',
                type: 'assistant',
                tool_calls: [],
                metadata: {
                    isTypingMessage: true,
                },
                content: '',
            };
            this.typingService.reset();

            // Keep completed next_actions messages unless explicitly clearing
            if (clearCompletedMessages) {
                this.toolMessages = [];
            } else {
                // Keep only completed progress messages (next_actions)
                this.toolMessages = this.toolMessages.filter(
                    (msg) => isNextActionsProgress(msg) && msg.complete,
                );
            }

            this.errorMessage = null;
            this.fullMessageReceived = false;
            this.reasoningStreamMessage = { id: 'reasoning-stream', type: 'reasoning', content: '' };
            this.lastReasoningIndex = -1;
            this.reasoningHeaderParsed = false;
            this.nextActionsProcessing = false;
            this.nextActionsMessage = null;
        },
        async sendConversationRequest () {
            this.continueStreaming = false;
            this.streamEndComplete = false;
            this.reset();
            let agentType;
            if (this.isCustomerFinder) {
                agentType = 'customer';
            } else if (this.isListenAgent) {
                agentType = 'listen';
            } else if (this.hasSegmentFlag('useClaudeAgent')) {
                agentType = 'agent';
            } else {
                agentType = 'chat';
            }
            await this.sendConversation({
                id: this.id,
                type: agentType,
                revision: this.$route?.query?.revision,
                onStreamRead: (data) => this.handleStreamData(data),
                onStreamEnd: () => this.handleStreamEnd(),
            });
        },
        async sendMessage (content, metadata) {
            this.errorMessage = null;

            await this.addMessage({
                conversationId: this.id,
                type: 'user',
                content,
                metadata,
            });

            // Track prompt submission
            this.trackAgent('prompt', {
                content,
                messageId: `msg_${Date.now()}`,
                fileUploaded: metadata?.files?.length > 0,
            });

            try {
                await this.sendConversationRequest();
            } catch (error) {
                this.reset();
                this.errorMessage = error.message || 'An error occurred while generating the response.';
            }
        },
        async handleUpdate (content) {

            const metadata = { ...this.messageMetadata };

            const { text, files } = content || {};
            if (files?.length) {
                metadata.files = files;
            }

            const lastMessage = this.conversation?.messages?.at(-1);
            let fullText;
            if (lastMessage?.metadata?.heading_type === 'ask_question') {
                fullText = `${lastMessage.content}\n\n${text}`;
            } else {
                fullText = text;
            }

            await this.sendMessage(fullText, metadata);
        },
        async handleRetry (content) {
            this.errorMessage = null;

            try {
                // Remove the last user + assistant exchange from the UI
                await this.removeLastExchange({ conversationId: this.id });
                await this.handleUpdate(content);
            } catch (error) {
                this.reset();
                this.errorMessage = error?.message || 'An error occurred while resubmitting the message.';
            }
        },
        handleStop () {
            // Determine behavior based on whether backend has finished sending the complete message
            if (this.fullMessageReceived) {
                // Reset all streaming message states - the complete message should already
                // be in conversation.messages from the backend's onEnd handler, so we don't
                // need to add it again.
                this.reset();
            } else {
                // Backend NOT finished - we only have a partial message
                // Call stopResponse to prevent backend from committing when it finishes
                this.stopResponse({ conversationId: this.id });

                // Add the partial message to the conversation so it stays visible
                if (this.typingMessage.content.trim()) {
                    this.addMessage({
                        conversationId: this.id,
                        type: 'assistant',
                        content: this.typingMessage.content,
                        metadata: { partial: true }, // Mark as partial message
                    });
                }

                // Reset the streaming message states.
                this.reset();
            }
        },
        handleStreamData (data) {
            // Check if we should ignore this data because stop was called or streaming is done
            if (!(this.pending || this.typing) || data.type === 'done') {
                return;
            }

            const isFinalResponse = data.type === 'assistant' && !data.tool_calls?.length && data.content;

            // Check for continue_streaming metadata on assistant messages
            if (data.type === 'assistant' && data.metadata?.continue_streaming === true) {
                this.continueStreaming = true;
            } else if (isFinalResponse && this.continueStreaming && !data.metadata?.continue_streaming) {
                // Final assistant message arrived - stop waiting for more content
                this.continueStreaming = false;
            }

            if (isToolCallArgumentStream(data) || isUserMessage(data)) return;

            if (isToolCall(data) || isToolResponse(data) || isProgressMessage(data) || isCustomInstructionsMessage(data)) {
                this.toolMessages.push(data);

                return;
            }

            const contentBlock = parseMessageContent(data);

            // 1) New "reasoning" stream chunks (plain text tokens)
            if (hasReasoningSummaries(data)) {
                const chunk = contentBlock.reasoningSummaries[0].text;
                const idx = contentBlock.reasoningSummaries[0].index;

                // when a new section starts, swap: clear the buffer instead of appending
                if (idx !== this.lastReasoningIndex) {
                    this.reasoningStreamMessage.id = `${contentBlock.id}-${idx}`;
                    this.reasoningStreamMessage.content = '';
                    this.lastReasoningIndex = idx;
                    this.reasoningHeaderParsed = false;
                }

                if (chunk && !this.reasoningHeaderParsed) {
                    this.reasoningStreamMessage.content += chunk;

                    // 1) Prefer bold header: keep only the first **...**
                    const boldHeader = this.reasoningStreamMessage.content.match(/^\s*\*\*([\s\S]*?)\*\*/);

                    if (boldHeader) {
                        this.reasoningStreamMessage.content = `${boldHeader[1].trim()}...`;
                        this.reasoningHeaderParsed = true;
                    }
                }

                const pos = this.toolMessages.findIndex((msg) => msg.id === this.reasoningStreamMessage.id);
                if (pos === -1) this.toolMessages.push(this.reasoningStreamMessage);
                else this.toolMessages.splice(pos, 1, this.reasoningStreamMessage);

                return;
            }

            // Handle next_actions progress events
            if (data.type === 'next_actions') {
                const isProcessing = ['start', 'analyzing', 'creating'].includes(data.status);
                const isComplete = data.status === 'complete';

                if (isProcessing) {
                    this.nextActionsProcessing = true;
                    this.toolMessages = this.toolMessages.filter((msg) => msg.id !== 'next-actions-progress');

                    const progressMessage = {
                        id: 'next-actions-progress',
                        type: 'progress',
                        content: data.message || 'Processing...',
                        metadata: { isNextActions: true },
                    };

                    this.toolMessages.push(progressMessage);
                    this.nextActionsMessage = progressMessage;
                } else if (isComplete) {
                    this.nextActionsProcessing = false;
                    this.nextActionsMessage = null;

                    const existingMessage = this.toolMessages.find((msg) => msg.id === 'next-actions-progress');
                    if (existingMessage) {
                        existingMessage.content = data.message || 'Completed segment suggestions';
                        existingMessage.complete = true;
                    }
                }

                return;
            }

            // Handle subagent_tool_call events
            if (data.type === 'subagent_tool_call') {
                if (data.status === 'start') {
                    this.toolMessages.push({
                        id: `subagent-tool-call-${data.tool_call_id}`,
                        type: 'assistant',
                        tool_calls: [{ name: data.tool_name, id: data.tool_call_id }],
                        subagent_call_id: data.subagent_call_id,

                    });
                } else if (data.status === 'complete') {
                    this.toolMessages.push({
                        id: `subagent-tool-response-${data.tool_call_id}`,
                        type: 'tool',
                        tool_call_id: data.tool_call_id,
                        subagent_call_id: data.subagent_call_id,
                    });
                }

                return;
            }

            if (data.type === 'artifact' && data.artifact_type === 'document') {
                if (data.status === 'started') {
                    this.typingMessage.metadata.artifact = {
                        type: 'document',
                        artifact_id: data.artifact_id,
                        data: {
                            content: '',
                        },
                    };
                    this.inlineArtifacts = false;
                    this.activeArtifactId = data.artifact_id;
                }
                if (data.status === 'title') {
                    this.typingMessage.metadata.artifact.name = data.title;
                }
                if (data.status === 'streaming') {
                    this.typingMessage.metadata.artifact.data.content += data.content;
                }
            }

            if (data.type === 'artifact' && data.artifact === 'markdown_table') {
                if (data.data === 'start') {
                    this.typingMessage.metadata.artifact = {
                        artifact_id: data.artifact_id,
                        type: 'markdown_table',
                        data: '',
                        name: 'Markdown table',
                    };
                } else if (data !== 'complete') {
                    this.typingMessage.metadata.artifact.data += data.data;
                }
            }

            if (!contentBlock.content || !isAssistantMessage(data)) return;
            const tokens = contentBlock.content.match(/\s+|\S+/g);
            this.typingService.addTokens(tokens);
        },
        handleStreamEnd () {
            if (this.continueStreaming) {
                return;
            }

            this.fullMessageReceived = true;

            // Capture tools used before reset clears toolMessages
            const toolsUsed = this.getToolsUsed();

            // Capture completed next_actions message before reset
            const completedNextActionsMessage = this.toolMessages.find(
                (msg) => msg.id === 'next-actions-progress' && msg.complete,
            );

            // Capture custom_instructions message before reset
            const customInstructionsMessage = this.toolMessages.find(isCustomInstructionsMessage);
            // Reset but keep completed messages
            this.reset(false);

            // Track final assistant response (after store commit in onEnd)
            try {
                const lastAssistant = this.lastAssistantMessage;
                const contentBlock = parseMessageContent(lastAssistant);

                if (lastAssistant && contentBlock.content) {
                    this.trackAgent('agent_response', {
                        content: contentBlock.content,
                        messageId: lastAssistant.id,
                        toolsUsed,
                    });
                }

                // Add completed next_actions message to persistent array
                if (completedNextActionsMessage) {
                    const lastAssistant = this.lastAssistantMessage;

                    if (lastAssistant) {
                        const progressMessage = {
                            id: `next-actions-${lastAssistant.id}`,
                            type: 'progress',
                            content: completedNextActionsMessage.content,
                            metadata: {
                                isNextActions: true,
                                assistantId: lastAssistant.id,
                            },
                            complete: true,
                        };

                        // Check if we already have this message (avoid duplicates)
                        const exists = this.persistentNextActionsMessages.some(
                            (msg) => msg.id === progressMessage.id,
                        );

                        if (!exists) {
                            this.persistentNextActionsMessages.push(progressMessage);
                        }
                    }
                }

                // Add custom_instructions message to persistent array
                if (customInstructionsMessage) {
                    const lastAssistant = this.lastAssistantMessage;

                    if (lastAssistant) {
                        const persistedMessage = {
                            id: `custom-instructions-${lastAssistant.id}`,
                            type: 'custom_instructions',
                            status: customInstructionsMessage.status,
                            message: customInstructionsMessage.message,
                            metadata: {
                                assistantId: lastAssistant.id,
                            },
                        };

                        // Check if we already have this message (avoid duplicates)
                        const exists = this.persistentCustomInstructionsMessages.some(
                            (msg) => msg.id === persistedMessage.id,
                        );

                        if (!exists) {
                            this.persistentCustomInstructionsMessages.push(persistedMessage);
                        }
                    }
                }

            } catch {
                // no-op
            }

            this.streamEndComplete = true;

            EventBus.$emit('conversation:saved');
        },
        getToolsUsed () {
            // Collect all tool call names (in order) from tool call messages for this streaming response
            try {
                const names = (this.toolMessages || [])
                    .filter((message) => isToolCall(message))
                    .flatMap((message) => (message.tool_calls || []).map((toolCall) => toolCall?.name))
                    .filter(Boolean);

                return names;
            } catch {
                return [];
            }
        },
        scrollThreadToBottom () {
            this.$refs.thread?.scrollToBottom();
        },
        getMessageArtifacts (message) {
            if (this.isTypingMessage(message)) {
                const { artifact } = message.metadata;

                return artifact ? [artifact] : [];
            }

            if (!this.conversation.artifacts) return [];

            const artifactIds = message.metadata?.artifact_ids || [];

            return artifactIds.map((id) => this.conversation.artifacts[id]).filter(Boolean);
        },
        getNextActionArtifacts (message) {
            return this.getMessageArtifacts(message).filter((artifact) => {
                // Only include next_actions type
                if (artifact.type !== 'next_actions') {
                    return false;
                }

                // For create_segment actions, validate required data
                if (artifact.action_type === 'create_segment' && artifact.data?.type) {
                    const { data } = artifact;

                    // Map of segment types to their required ID field
                    const segmentTypeValidation = {
                        visitor: () => (data.visitorIds?.length || 0) > 0,
                        account: () => (data.accountIds?.length || 0) > 0,
                        page: () => !!data.pageId,
                        feature: () => !!data.featureId,
                        trackEvent: () => !!data.trackTypeId,
                    };

                    const validator = segmentTypeValidation[data.type];

                    return validator ? validator() : false;
                }

                // Include all other action types
                return true;
            });
        },
        getNonNextActionArtifacts (message) {
            return this.getMessageArtifacts(message).filter(
                (artifact) => artifact.type !== 'next_actions',
            );
        },
        getNextActionsHeading (message) {
            // Will likely have other headings as we expand next actions
            const headingType = message.metadata?.heading_type;
            if (headingType === 'ask_question') {
                return this.$t('askPendo.conversation.Suggested questions');
            }

            return this.$t('askPendo.conversation.What would you like to do next?');
        },
        handleNextAction (artifact) {
            // Future: add handlers for create_report, view_details
            switch (artifact.action_type) {
                case 'ask_question':
                    this.handleAskQuestion(artifact);
                    break;
                case 'create_segment':
                    this.handleCreateSegment(artifact);
                    break;
                case 'create_guide':
                    this.handleCreateGuide(artifact);
                    break;
            }
        },
        async handleAskQuestion (artifact) {
            const metadata = { ...this.messageMetadata };

            const question = `${artifact.data?.prompt}\n\n${artifact.data?.question}`;

            this.sendMessage(question, metadata);
        },
        async handleCreateSegment (artifact) {
            // Store the artifact for reference
            this.segmentOverlayData = artifact;

            // Transform artifact data into filter format
            const filters = transformArtifactToFilters(artifact.data);

            // Handle empty filters - don't proceed if no valid data
            if (filters.length === 0) {
                console.error('No valid filters generated from artifact data');

                return;
            }

            // Use the artifact name directly
            const segmentName = artifact.name || this.$t('askPendo.conversation.Unnamed Segment');

            try {
                // Create a new segment with pre-filled data from the artifact
                await this.createSegmentWithDefinition({
                    name: segmentName,
                    description: '',
                    shared: true, // Default to "Everyone" for Ask Pendo segments
                    definition: {
                        filters,
                    },
                });

                this.showSegmentOverlay = true;
            } catch (error) {
                console.error('Failed to create segment:', error);
            }
        },
        async handleCreateGuide (artifact) {
            const artifactId = artifact?.artifact_id;
            const entityType = artifact?.action_type?.replace('create_', '') || 'item';

            if (artifactId) {
                this.updateArtifact({
                    conversationId: this.id,
                    artifactId,
                    updates: {
                        completed: true,
                        completed_entity_id: null,
                        completed_entity_type: entityType,
                        completed_at: new Date().toISOString(),
                    },
                });

                try {
                    await putAskPendoConversation(this.conversation);
                } catch (error) {
                    console.error('Failed to update conversation:', error);

                    this.updateArtifact({
                        conversationId: this.id,
                        artifactId,
                        updates: {
                            completed: false,
                            completed_entity_id: undefined,
                            completed_entity_type: undefined,
                            completed_at: undefined,
                        },
                    });

                    PendoNotification({
                        type: 'error',
                        title: this.$t('askPendo.nextActions.Error updating conversation'),
                        message: this.$t('askPendo.nextActions.Please try again'),
                    });

                    return;
                }
            }

            let content = this.$t('askPendo.guide.Create a guide');
            const segmentId = get(artifact, 'data.segment_id', null);

            if (segmentId) {
                content = this.$t('askPendo.guide.Create a guide for segment {segmentId}', {
                    segmentId,
                });
            }

            this.sendMessage(content, {});
        },
        onCloseSegmentOverlay () {
            this.showSegmentOverlay = false;
            this.segmentOverlayData = null;
        },
        async onSaveSegment (segment) {
            this.showSegmentOverlay = false;
            const artifactId = this.segmentOverlayData?.artifact_id;
            const entityType = this.segmentOverlayData?.action_type?.replace('create_', '') || 'segment';
            this.segmentOverlayData = null;

            if (artifactId) {
                this.updateArtifact({
                    conversationId: this.id,
                    artifactId,
                    updates: {
                        completed: true,
                        completed_entity_id: segment.id,
                        completed_entity_type: entityType,
                        completed_at: new Date().toISOString(),
                    },
                });
            }

            appendGuideNextActionArtifact(this.conversation, this.$t, { segment });

            try {
                await putAskPendoConversation(this.conversation);
            } catch (error) {
                console.error('Failed to update conversation:', error);

                PendoNotification({
                    type: 'error',
                    title: this.$t('askPendo.nextActions.Error updating conversation'),
                    message: this.$t('askPendo.nextActions.Please try again'),
                });
            }
        },
        reasoningMessages (message) {
            if (this.isTypingMessage(message) || this.isReasoning(message)) {
                return this.toolMessages;
            }

            return this.reasoningMessagesById[message.id] || [];
        },
        reasoningUi (message) {
            const isTyping = this.isTypingMessage(message) || this.isReasoning(message);
            const msgs = this.reasoningMessages(message);
            const hasMsgs = msgs && msgs.length > 0;

            // "Heads up" message with continue_streaming - hide content, show pending
            if (message.metadata?.continue_streaming) {
                return { show: false, pending: true, msgs };
            }

            // Waiting for more content in multi-part stream - show loading
            if (isTyping && this.continueStreaming) {
                return { show: true, pending: true, msgs };
            }

            // Typing message - show if not started, has reasoning, or processing next actions
            if (isTyping) {
                const show = !this.typingStarted || hasMsgs || this.nextActionsProcessing;
                const pending = !this.typingStarted || (this.nextActionsProcessing && !this.typing);

                return { show, pending, msgs };
            }

            // Regular assistant message - show reasoning stack if available
            return { show: isAssistantMessage(message) && hasMsgs, pending: false, msgs };
        },
        isTypingMessage (message) {
            return message.metadata?.isTypingMessage === true;
        },
        isReasoning (message) {
            return message?.type === 'reasoning';
        },
        handleNewConversation () {
            this.$router.push({
                path: this.landingPagePath,
                query: { ...this.$route.query },
            });
        },
        trackAgent (eventType, { content, messageId, toolsUsed = [], fileUploaded = false }) {
            const payload = buildPayload({
                agentId: this.agentId,
                conversationId: this.id,
                messageId,
                content,
                modelUsed: this.getEmployeeConfig?.model || null,
                suggestedPrompt: false,
                toolsUsed,
                fileUploaded,
            });
            trackAgentEvent(eventType, payload);
        },
        clearActiveArtifact () {
            this.activeArtifactId = null;
            this.inlineArtifacts = true;
        },
        setActiveArtifact (artifactItem) {
            const { id, messageId } = artifactItem;
            if (id) {
                this.activeArtifactId = id;
            }
            if (messageId) {
                this.$refs.thread?.scrollToMessage(messageId);
            }
        },
    },
};
</script>

<style scoped lang="less">
@import '@/ask-pendo/agent-mode-styles.less';

.ask-pendo-conversation {
    position: absolute;
    inset: 0;

    display: flex;
    flex-direction: column;

    &__header {
        flex-shrink: 0;
    }

    &__actions {
        display: flex;
        align-items: center;
        gap: 8px;

        align-self: flex-end;
    }

    :deep(.pendo-page-header) {
        min-height: 0;
        padding: 16px 16px 0 16px;

        .pendo-page-header__content h1 {
            margin-right: 0;
        }
    }

    &__content {
        display: flex;
        align-items: stretch;
        padding: 0;
        min-height: 0;
        background-color: var(--palette-gray-5);
    }

    &__pane {
        flex: 1;
    }

    &__thread {
        min-width: 0;
        display: flex;
        justify-content: center;
    }

    &__metrics {
        min-width: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    &__artifacts {
        padding: 16px;
        min-width: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    &__artifact-inline {
        margin: 24px 0;
    }

    &__title-tag {
        margin-left: 8px;
    }

    &__next-actions {
        margin-top: 16px;
    }

    &__next-actions-heading {
        font-size: 16px;
        color: var(--palette-gray-90);
        margin-bottom: 8px;
    }

    &__next-actions-list {
        padding-left: 24px;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 4px;

        li {
            font-size: 16px;
            font-weight: 600;
            line-height: 28px;
            color: var(--palette-teal-80);

            &:hover::marker {
                color: var(--palette-teal-70);
            }
        }
    }
}

.conversation-metrics__header {
    display: flex;
}

.close-artifact-button {
    margin: 8px 8px 8px auto;
}
</style>
